import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Modal, StatusBadge, TipoBadge, CategoriaBadge } from "@/components/ui-bits";
import { toastCancel, toastReschedule, toastPrenatal } from "@/lib/toasts";
import InstallPWA from "@/components/InstallPWA";
import {
  todayStr,
  addDaysStr,
  fmtDateLong,
  fmtDateShort,
  clinicHelpLink,
  patientConfirmLink,
  comprobanteWhatsAppLink,
  CATEGORIAS,
} from "@/lib/constants";
import {
  CalendarPlus,
  ClipboardList,
  LogOut,
  Clock,
  Loader2,
  CheckCircle2,
  CalendarX,
  RefreshCw,
  Baby,
  ChevronRight,
  MessageCircle,
  Copy,
  Upload,
  ShieldCheck,
} from "lucide-react";

const DOW = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

function DateStrip({ selected, onSelect, counts }) {
  const days = Array.from({ length: 30 }, (_, i) => addDaysStr(todayStr(), i));
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="date-strip">
      {days.map((d) => {
        const dateObj = new Date(d + "T00:00:00");
        const count = counts[d];
        const disabled = count === 0;
        const active = selected === d;
        return (
          <button
            key={d}
            data-testid={`date-chip-${d}`}
            onClick={() => !disabled && onSelect(d)}
            disabled={disabled}
            className={`flex flex-col items-center min-w-[64px] py-3 rounded-2xl border transition-all ${
              active
                ? "bg-[#2E6CA4] text-white border-[#2E6CA4] shadow-md"
                : disabled
                ? "bg-muted/50 text-muted-foreground/50 border-transparent cursor-not-allowed"
                : "bg-white border-border hover:border-[#2E6CA4] text-foreground"
            }`}
          >
            <span className="text-[11px] uppercase tracking-wide">{DOW[dateObj.getDay()]}</span>
            <span className="text-xl font-semibold leading-tight">{dateObj.getDate()}</span>
            <span className={`text-[10px] mt-0.5 ${active ? "text-white/80" : "text-muted-foreground"}`}>
              {count === undefined ? "…" : count === 0 ? "—" : `${count} libres`}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function BookingFlow({ onBooked }) {
  const { user } = useAuth();
  const [counts, setCounts] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({
    patient_name: user?.name || "",
    patient_phone: "",
    patient_email: user?.email || "",
    motivo: "",
    tipo: "primera",
    categoria: null,
    embarazada: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [payCfg, setPayCfg] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    api.get("/settings/public").then((r) => setPayCfg(r.data.payment_config)).catch((e) => console.error(e));
  }, []);

  // Temporizador de la reserva
  useEffect(() => {
    if (!reservation?.reservation_expires_at) return;
    const tick = () => {
      const diff = Math.floor((new Date(reservation.reservation_expires_at) - new Date()) / 1000);
      setSecondsLeft(diff > 0 ? diff : 0);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [reservation]);

  useEffect(() => {
    const load = async () => {
      try {
        const start = todayStr();
        const end = addDaysStr(start, 29);
        const res = await api.get("/availability/range", { params: { start, end } });
        setCounts(res.data);
      } catch (error) {
        console.error("No se pudo cargar la disponibilidad:", error);
        toast.error("No se pudo cargar la disponibilidad. Intenta de nuevo.");
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSelectedTime(null);
    api
      .get("/availability", { params: { date: selectedDate } })
      .then((res) => {
        const list = res.data.slots || [];
        setSlots(list);
        // Opción A: preselecciona el primer horario disponible para evitar huecos
        if (list.length > 0) setSelectedTime(list[0]);
      })
      .catch((error) => {
        console.error("No se pudieron cargar los horarios:", error);
        setSlots([]);
      })
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const submit = async () => {
    if (!form.patient_name.trim() || !form.patient_phone.trim()) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    if (!form.categoria) {
      toast.error("Selecciona el tipo de consulta");
      return;
    }
    if (!policyAccepted) {
      toast.error("Debes leer y aceptar la política de anticipo");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/appointments/reserve", {
        date: selectedDate,
        time: selectedTime,
        patient_name: form.patient_name,
        patient_phone: form.patient_phone,
        patient_email: form.patient_email,
        motivo: form.motivo,
        tipo: form.tipo,
        categoria: form.categoria,
        embarazada: form.embarazada,
        policy_accepted: true,
      });
      setReservation(res.data);
      setUploaded(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reservar el horario");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadComprobante = async (fileList) => {
    const file = fileList?.[0];
    if (!file || !reservation) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/appointments/${reservation.id}/comprobante`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(true);
      toast.success("Comprobante enviado. Tu pago está en revisión.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el comprobante");
    } finally {
      setUploading(false);
    }
  };

  const resetBooking = () => {
    setReservation(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setSlots([]);
    setPolicyAccepted(false);
    onBooked();
  };

  const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success("Copiado"); };
  const fmtTimer = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (reservation) {
    const bank = payCfg?.bank || {};
    const expired = secondsLeft <= 0;
    return (
      <div className="space-y-5" data-testid="payment-screen">
        <div className="card-surface p-5 text-center">
          <ShieldCheck className="mx-auto mb-2 text-[#2E6CA4]" size={34} />
          <p className="font-display text-2xl text-[#1F4E78]">Reserva temporal</p>
          <p className="text-sm text-muted-foreground">Tu horario ha sido apartado temporalmente.</p>
          {!expired ? (
            <p className="mt-3 inline-block px-4 py-2 rounded-full font-mono font-bold text-lg" style={{ background: "#FFF4E5", color: "#B45309" }} data-testid="reservation-timer">
              ⏳ {fmtTimer(secondsLeft)}
            </p>
          ) : (
            <p className="mt-3 text-sm font-semibold text-red-600">El tiempo para confirmar terminó. El horario fue liberado, realiza una nueva reserva.</p>
          )}
        </div>

        {!expired && (
          <>
            <div className="card-surface p-5 space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">Folio</span><span className="font-mono font-bold text-[#1F4E78]" data-testid="pay-folio">{reservation.folio}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">Servicio</span><span className="font-semibold text-sm">{reservation.service_label || reservation.categoria}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">Fecha</span><span className="text-sm">{fmtDateLong(reservation.date)} · {reservation.time} hrs</span></div>
              <hr className="border-border" />
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">Anticipo</span><span className="font-bold text-[#2E6CA4]">${reservation.deposit_amount} MXN</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">Saldo pendiente</span><span className="text-sm">${reservation.remaining_amount} MXN</span></div>
            </div>

            <div className="card-surface p-5 space-y-3">
              <p className="font-semibold text-[#1F4E78]">Datos para tu anticipo</p>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Banco</span><span>{bank.banco}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Titular</span><span className="text-right">{bank.titular}</span></div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Cuenta</span>
                  <span className="flex items-center gap-2"><span className="font-mono">{bank.cuenta}</span>
                    <button onClick={() => copyText(bank.cuenta)} data-testid="copy-cuenta" className="text-[#2E6CA4]"><Copy size={15} /></button></span>
                </div>
                <div className="flex justify-between"><span className="text-muted-foreground">Concepto</span><span>{bank.concepto}</span></div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Referencia</span>
                  <span className="flex items-center gap-2"><span className="font-mono font-bold">{reservation.payment_reference}</span>
                    <button onClick={() => copyText(reservation.payment_reference)} data-testid="copy-reference" className="text-[#2E6CA4]"><Copy size={15} /></button></span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Verifica los datos antes de transferir. La referencia es única para tu cita.</p>
              <p className="text-sm font-semibold rounded-lg px-3 py-2" style={{ background: "#EAF8EF", color: "#15803D" }}>
                DEBERÁS ENVIAR TU COMPROBANTE AL WHATSAPP 55-70-46-60-65
              </p>
            </div>

            <div className="card-surface p-5 space-y-3">
              {uploaded ? (
                <div className="text-center py-2">
                  <CheckCircle2 className="mx-auto mb-2 text-green-600" size={30} />
                  <p className="font-semibold text-[#1F4E78]">¡Comprobante enviado!</p>
                  <p className="text-sm text-muted-foreground">Tu pago está en revisión. Te confirmaremos tu cita en breve.</p>
                </div>
              ) : (
                <>
                  <p className="font-semibold text-[#1F4E78]">Envía tu comprobante</p>
                  <label className={`btn-outline w-full py-3 flex items-center justify-center gap-2 cursor-pointer ${uploading ? "opacity-60" : ""}`} data-testid="upload-comprobante">
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {uploading ? "Subiendo..." : "Subir comprobante (foto o PDF)"}
                    <input type="file" accept="image/*,application/pdf" hidden disabled={uploading} onChange={(e) => uploadComprobante(e.target.files)} />
                  </label>
                  <a href={comprobanteWhatsAppLink(reservation, payCfg)} target="_blank" rel="noreferrer" data-testid="send-comprobante-wa" className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                    <MessageCircle size={16} /> Enviar por WhatsApp
                  </a>
                </>
              )}
            </div>
          </>
        )}
        <button onClick={resetBooking} className="w-full py-3 text-sm text-muted-foreground" data-testid="payment-done">
          {uploaded || expired ? "Volver al inicio" : "Ya realicé mi pago / Volver"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card-surface p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#2E6CA4] mb-3">
          1 · Elige el día
        </p>
        <DateStrip selected={selectedDate} onSelect={setSelectedDate} counts={counts} />
      </div>

      {selectedDate && (
        <div className="card-surface p-5 animate-fadeup">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#2E6CA4] mb-1">
            2 · Elige la hora
          </p>
          <p className="text-sm text-muted-foreground mb-4 capitalize">{fmtDateLong(selectedDate)}</p>
          {loadingSlots ? (
            <div className="flex justify-center py-6">
              <Loader2 className="animate-spin text-[#2E6CA4]" />
            </div>
          ) : slots.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">
              No hay horarios disponibles este día.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3 text-xs text-[#1F4E78] bg-[#EDF3F9] rounded-lg px-3 py-2">
                <CheckCircle2 size={14} className="text-[#2E6CA4] shrink-0" />
                Te sugerimos el <strong>primer horario disponible</strong> para agilizar tu atención. Puedes cambiarlo si lo prefieres.
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slots-grid">
                {slots.map((s, idx) => {
                  const active = selectedTime === s;
                  const suggested = idx === 0;
                  return (
                    <button
                      key={s}
                      data-testid={`slot-${s}`}
                      onClick={() => setSelectedTime(s)}
                      className={`relative py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                        active
                          ? "bg-[#2E6CA4] text-white border-[#2E6CA4] shadow"
                          : "bg-white border-border hover:border-[#2E6CA4]"
                      } ${suggested ? "blink-suggested" : ""}`}
                    >
                      {suggested && (
                        <span
                          className={`absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                            active ? "bg-white text-[#2E6CA4]" : "bg-[#2E6CA4] text-white"
                          }`}
                        >
                          Sugerido
                        </span>
                      )}
                      {s}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {selectedTime && (
        <div className="card-surface p-5 animate-fadeup">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#2E6CA4] mb-4">
            3 · Tus datos
          </p>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Nombre completo *</label>
              <input
                data-testid="booking-name"
                className="field"
                value={form.patient_name}
                onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                placeholder="Tu nombre"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Teléfono (WhatsApp) *</label>
                <input
                  data-testid="booking-phone"
                  className="field"
                  value={form.patient_phone}
                  onChange={(e) => setForm({ ...form, patient_phone: e.target.value })}
                  placeholder="52 55 1234 5678"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Correo (opcional)</label>
                <input
                  data-testid="booking-email"
                  className="field"
                  value={form.patient_email}
                  onChange={(e) => setForm({ ...form, patient_email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Tipo de consulta *</label>
              <div className="grid grid-cols-2 gap-2" data-testid="categoria-buttons">
                {CATEGORIAS.map((c) => {
                  const active = form.categoria === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      data-testid={`categoria-${c.key}`}
                      onClick={() => setForm({ ...form, categoria: c.key })}
                      className="py-3 px-3 rounded-xl text-sm font-semibold border-2 transition-all text-left flex items-center gap-2"
                      style={{
                        background: active ? c.bg : "#fff",
                        color: active ? c.color : "#475569",
                        borderColor: active ? c.color : "#E2E8F0",
                      }}
                    >
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Motivo (opcional)</label>
              <textarea
                data-testid="booking-motivo"
                className="field min-h-[72px] resize-none"
                value={form.motivo}
                onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                placeholder="Describe brevemente el motivo de tu consulta"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <select
                data-testid="booking-tipo"
                className="field flex-1 min-w-[160px]"
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              >
                <option value="primera">Primera consulta</option>
                <option value="subsecuente">Subsecuente</option>
              </select>
              <button
                type="button"
                data-testid="booking-embarazada"
                onClick={() => setForm({ ...form, embarazada: !form.embarazada })}
                className={`flex items-center gap-2 px-4 rounded-xl border font-medium transition-all ${
                  form.embarazada
                    ? "bg-[#FDE7F0] border-[#E48FB5] text-[#9D255E]"
                    : "bg-white border-border text-muted-foreground"
                }`}
              >
                <Baby size={18} /> Embarazada
              </button>
            </div>

            {payCfg && form.categoria && payCfg.services?.[form.categoria] && (
              <div className="rounded-xl p-3 mb-3 text-sm" style={{ background: "#EDF3F9" }} data-testid="deposit-summary">
                <div className="flex justify-between"><span className="text-muted-foreground">Precio total</span><span className="font-semibold">${payCfg.services[form.categoria].price} MXN</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Anticipo para reservar</span><span className="font-bold text-[#2E6CA4]">${payCfg.services[form.categoria].deposit} MXN</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Saldo en consulta</span><span>${Math.max(0, payCfg.services[form.categoria].price - payCfg.services[form.categoria].deposit)} MXN</span></div>
              </div>
            )}

            <label className="flex items-start gap-2 mb-3 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={policyAccepted} onChange={(e) => setPolicyAccepted(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#2E6CA4]" data-testid="policy-checkbox" />
              <span>He leído y acepto la <button type="button" onClick={() => setShowPolicy(true)} className="text-[#2E6CA4] underline font-semibold" data-testid="open-policy">política de anticipo y cancelación</button>. Para reservar se solicita un anticipo que se descuenta del costo total.</span>
            </label>

            <button
              data-testid="booking-submit"
              onClick={submit}
              disabled={submitting}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
              Reservar y pagar anticipo · {selectedTime}
            </button>
          </div>
        </div>
      )}

      <Modal open={showPolicy} onClose={() => setShowPolicy(false)} title="Política de anticipo">
        <div className="space-y-2 text-sm text-muted-foreground" data-testid="policy-modal">
          <p>El anticipo se descuenta del costo total de tu consulta.</p>
          <p>Si tu cita fue agendada con <b>12 horas o más</b> de anticipación, el anticipo será reembolsable únicamente si cancelas o reagendas con al menos 12 horas de anticipación.</p>
          <p>Si cancelas, reagendas o no te presentas con <b>menos de 12 horas</b> de anticipación, el anticipo no será reembolsable.</p>
          <p>Las citas solicitadas con menos de 12 horas de anticipación tienen una política especial: el anticipo no será reembolsable.</p>
          <p>Si la cancelación ocurre por parte del consultorio, el anticipo podrá reembolsarse o transferirse a una nueva cita.</p>
          <button onClick={() => { setPolicyAccepted(true); setShowPolicy(false); }} className="btn-primary w-full py-2.5 mt-2" data-testid="accept-policy">Entendido, acepto</button>
        </div>
      </Modal>
    </div>
  );
}

function RescheduleModal({ appt, open, onClose, onDone }) {
  const [date, setDate] = useState(appt?.date || todayStr());
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setTime(null);
    api
      .get("/availability", { params: { date } })
      .then((r) => setSlots(r.data.slots))
      .catch((error) => {
        console.error("No se pudieron cargar los horarios:", error);
        setSlots([]);
      })
      .finally(() => setLoading(false));
  }, [date, open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/appointments/${appt.id}`, { date, time });
      toastReschedule("Cita reagendada");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reagendar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Reagendar cita" testid="reschedule-modal">
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-1.5 block">Nueva fecha</label>
          <input
            type="date"
            className="field"
            min={todayStr()}
            value={date}
            data-testid="reschedule-date"
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="animate-spin text-[#2E6CA4]" /></div>
        ) : slots.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-3">Sin horarios disponibles.</p>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {slots.map((s) => (
              <button
                key={s}
                onClick={() => setTime(s)}
                className={`py-2 rounded-lg text-sm font-semibold border ${
                  time === s ? "bg-[#2E6CA4] text-white border-[#2E6CA4]" : "bg-white border-border"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <button
          disabled={!time || saving}
          onClick={save}
          data-testid="reschedule-save"
          className="btn-primary w-full py-2.5 flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} Guardar cambios
        </button>
      </div>
    </Modal>
  );
}

function MyAppointments({ refreshKey, onChanged }) {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reschedule, setReschedule] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/appointments")
      .then((r) => setAppts(r.data))
      .catch((error) => {
        console.error("No se pudieron cargar las citas:", error);
        toast.error("No se pudieron cargar tus citas. Intenta de nuevo.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta cita?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      toastCancel("Cita cancelada");
      load();
      onChanged();
    } catch (error) {
      console.error("No se pudo cancelar la cita:", error);
      toast.error("No se pudo cancelar");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#2E6CA4]" /></div>;
  }
  if (appts.length === 0) {
    return (
      <div className="card-surface p-10 text-center">
        <ClipboardList className="mx-auto text-muted-foreground/50 mb-3" size={40} />
        <p className="text-muted-foreground">Aún no tienes citas agendadas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {appts.map((a) => {
        const past = a.status === "cancelada" || a.status === "asistio" || a.status === "no_asistio";
        return (
          <div key={a.id} className="card-surface p-5 animate-fadeup" data-testid={`my-appt-${a.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="font-display text-2xl text-[#1F4E78] capitalize leading-tight">
                  {fmtDateShort(a.date)}
                </p>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                  <Clock size={14} /> {a.time} hrs
                </p>
              </div>
              <StatusBadge status={a.status} />
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <CategoriaBadge categoria={a.categoria} />
              <TipoBadge tipo={a.tipo} />
              {a.embarazada && (
                <span className="chip flex items-center gap-1" style={{ background: "#FDE7F0", color: "#9D255E" }}>
                  <Baby size={13} /> Embarazada
                </span>
              )}
            </div>
            {a.motivo && <p className="text-sm text-muted-foreground mb-3">{a.motivo}</p>}
            {!past && (
              <div className="flex gap-2 pt-2 border-t border-border">
                <button
                  onClick={() => setReschedule(a)}
                  data-testid={`reschedule-btn-${a.id}`}
                  className="btn-outline flex-1 py-2 text-sm flex items-center justify-center gap-1.5"
                >
                  <RefreshCw size={15} /> Reagendar
                </button>
                <button
                  onClick={() => cancel(a.id)}
                  data-testid={`cancel-btn-${a.id}`}
                  className="flex-1 py-2 text-sm rounded-full font-semibold border border-[#f3c2c2] text-[#b91c1c] hover:bg-[#fef2f2] flex items-center justify-center gap-1.5 transition-colors"
                >
                  <CalendarX size={15} /> Cancelar
                </button>
              </div>
            )}
          </div>
        );
      })}
      {reschedule && (
        <RescheduleModal
          appt={reschedule}
          open={!!reschedule}
          onClose={() => setReschedule(null)}
          onDone={() => {
            load();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

export default function PatientApp() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("agendar");
  const [refreshKey, setRefreshKey] = useState(0);
  const [prenatalDue, setPrenatalDue] = useState(null);

  useEffect(() => {
    api
      .get("/prenatal/my-reminder")
      .then((r) => {
        if (r.data?.due) {
          setPrenatalDue(r.data);
          toastPrenatal();
        }
      })
      .catch((e) => console.error("No se pudo verificar el seguimiento prenatal:", e));
  }, [refreshKey]);

  return (
    <div className="min-h-screen pb-32">
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo size={40} />
            <div>
              <p className="font-display text-xl leading-none text-[#1F4E78]">Dr. Jairo Franco</p>
              <p className="text-[11px] text-muted-foreground">Ginecólogo · Agenda tu cita</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <InstallPWA variant="ghost" className="hidden sm:flex" />
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="p-2 rounded-full hover:bg-muted text-muted-foreground transition-colors"
              title="Cerrar sesión"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-6">
        <div className="mb-6">
          <p className="text-sm text-muted-foreground">Hola{user?.name ? "," : ""}</p>
          <h1 className="font-display text-3xl text-[#1F4E78]">{user?.name || "Bienvenida"}</h1>
        </div>

        {prenatalDue && (
          <div
            data-testid="prenatal-reminder-banner"
            className="mb-6 rounded-2xl p-4 flex items-start gap-3 animate-fadeup"
            style={{ background: "#FDE7F0", border: "1px solid #F0A8C6" }}
          >
            <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FBCFE2" }}>
              <Baby size={20} style={{ color: "#DB2777" }} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold" style={{ color: "#9D255E" }}>
                Es momento de tu siguiente control prenatal
              </p>
              <p className="text-sm mt-0.5" style={{ color: "#B84A78" }}>
                Han pasado {prenatalDue.days_since} días desde tu última cita. Agenda tu próximo control para cuidar tu embarazo.
              </p>
              <button
                data-testid="prenatal-book-btn"
                onClick={() => setTab("agendar")}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-white text-sm transition-colors"
                style={{ background: "#DB2777" }}
              >
                <CalendarPlus size={16} /> Agendar mi siguiente control
              </button>
            </div>
          </div>
        )}

        <div className="flex p-1 rounded-full bg-[#EDF3F9] mb-6 sticky top-[60px] z-20">
          <button
            data-testid="patient-tab-agendar"
            onClick={() => setTab("agendar")}
            className={`flex-1 py-2.5 text-sm rounded-full font-semibold flex items-center justify-center gap-1.5 transition-all ${
              tab === "agendar" ? "bg-white text-[#2E6CA4] shadow-sm" : "text-muted-foreground"
            }`}
          >
            <CalendarPlus size={16} /> Agendar
          </button>
          <button
            data-testid="patient-tab-mis-citas"
            onClick={() => setTab("citas")}
            className={`flex-1 py-2.5 text-sm rounded-full font-semibold flex items-center justify-center gap-1.5 transition-all ${
              tab === "citas" ? "bg-white text-[#2E6CA4] shadow-sm" : "text-muted-foreground"
            }`}
          >
            <ClipboardList size={16} /> Mis citas
          </button>
        </div>

        {tab === "agendar" ? (
          <BookingFlow
            onBooked={() => {
              setRefreshKey((k) => k + 1);
              setTab("citas");
            }}
          />
        ) : (
          <MyAppointments refreshKey={refreshKey} onChanged={() => setRefreshKey((k) => k + 1)} />
        )}

        <a
          href={clinicHelpLink()}
          target="_blank"
          rel="noreferrer"
          data-testid="clinic-whatsapp-help"
          className="mt-8 card-surface p-4 flex items-center gap-3 hover:shadow-md transition-shadow"
        >
          <span className="w-11 h-11 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: "#25D366" }}>
            <MessageCircle size={22} />
          </span>
          <div>
            <p className="font-semibold text-[#1F4E78] leading-tight">¿No tienes correo o requieres otro horario?</p>
            <p className="text-sm text-muted-foreground">Escríbenos por WhatsApp y te ayudamos a agendar.</p>
          </div>
        </a>

        <div className="mt-4 sm:hidden">
          <InstallPWA variant="solid" />
        </div>
      </main>
    </div>
  );
}
